/**
 * Accordion state for the sidebar rail: which module group is open.
 *
 * ## Why the rail is an accordion at all
 *
 * Twenty-five modules plus five group labels is roughly 1,110px of rail
 * against about 680px of available height at 1280x800 - the smallest
 * supported viewport. Measured against the shipped rail metrics on the MUR
 * page "Information Architecture and Navigation", two groups open is 582px
 * (no headroom) and three is 752px (overflows). A fixed rail cannot hold
 * the platform, and a second scroll region beside the content area is worse
 * than a fold.
 *
 * So: one group open at a time. Navigating to a route opens its group and
 * closes the others, which means the current module is always visible
 * without hunting and the rail never scrolls.
 *
 * ## The route wins
 *
 * The open group is derived from the current route first and the stored
 * preference second. That ordering matters: if the stored group and the
 * route disagree, honouring storage would hide the page you are looking at
 * from the navigation. The MUR page states the rule directly - "the current
 * module's group cannot be collapsed while you are inside it".
 *
 * `toggle` therefore refuses to close the current route's group. It is not
 * disabled in the DOM - a disabled control gives no feedback and reads as
 * broken - it simply has no effect, and the button keeps
 * `aria-expanded="true"` so assistive tech is told the truth.
 *
 * ## Storage
 *
 * The open group persists across sessions in `localStorage` under a key
 * holding nothing but a group name - no identifier, no tenant, no token -
 * so ADR-014 (session tokens in memory only) is untouched. An unknown or
 * corrupt stored value is ignored rather than trusted.
 */
import { useCallback, useEffect, useState } from 'react'

import type { NavGroupKey } from '../features/modules/module-registry'

/** Key holds a group name and nothing else. See the note on ADR-014 above. */
export const NAV_GROUP_STORAGE_KEY = 'murmuration.nav.openGroup'

/**
 * Read the persisted group, validating it against the groups that actually
 * exist. `localStorage` throws in a sandboxed iframe and in Safari's
 * private mode, so every access is guarded.
 */
function readStoredGroup(valid: readonly NavGroupKey[]): NavGroupKey | null {
  try {
    const raw = window.localStorage.getItem(NAV_GROUP_STORAGE_KEY)
    if (raw === null) return null
    return valid.includes(raw as NavGroupKey) ? (raw as NavGroupKey) : null
  } catch {
    return null
  }
}

function writeStoredGroup(group: NavGroupKey | null): void {
  try {
    if (group === null) {
      window.localStorage.removeItem(NAV_GROUP_STORAGE_KEY)
    } else {
      window.localStorage.setItem(NAV_GROUP_STORAGE_KEY, group)
    }
  } catch {
    // Storage unavailable. The accordion still works for this session; only
    // persistence is lost, which is not worth failing a render over.
  }
}

export interface NavAccordion {
  /** The single open group, or null when every group is collapsed. */
  readonly openGroup: NavGroupKey | null
  /** True when this group is open. */
  readonly isOpen: (group: NavGroupKey) => boolean
  /**
   * True when this group holds the current route, and so cannot be closed.
   */
  readonly isCurrent: (group: NavGroupKey) => boolean
  /** Open a closed group, or close an open one that is not the current one. */
  readonly toggle: (group: NavGroupKey) => void
}

export interface UseNavAccordionOptions {
  /** Every group key that exists, used to validate stored values. */
  readonly groups: readonly NavGroupKey[]
  /** The group holding the current route, or null if none does. */
  readonly currentGroup: NavGroupKey | null
}

export function useNavAccordion({ groups, currentGroup }: UseNavAccordionOptions): NavAccordion {
  const [openGroup, setOpenGroup] = useState<NavGroupKey | null>(
    () => currentGroup ?? readStoredGroup(groups),
  )

  // Navigating opens the destination's group and closes the others. Guarded
  // on inequality so this does not fight a deliberate toggle within the
  // same route.
  useEffect(() => {
    if (currentGroup !== null) {
      setOpenGroup((prev) => (prev === currentGroup ? prev : currentGroup))
    }
  }, [currentGroup])

  useEffect(() => {
    writeStoredGroup(openGroup)
  }, [openGroup])

  const toggle = useCallback(
    (group: NavGroupKey) => {
      setOpenGroup((prev) => {
        if (prev !== group) return group
        // Closing the group you are inside would hide the current page from
        // the nav. Refuse, and leave it open.
        if (group === currentGroup) return prev
        return null
      })
    },
    [currentGroup],
  )

  const isOpen = useCallback((group: NavGroupKey) => group === openGroup, [openGroup])
  const isCurrent = useCallback((group: NavGroupKey) => group === currentGroup, [currentGroup])

  return { openGroup, isOpen, isCurrent, toggle }
}
